import { Mic, RotateCcw, Square, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

const MAX_RECORDING_MS = 2 * 60 * 1000; // matches the 10MB server-side cap on task-audio uploads
const CANDIDATE_MIME_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']; // Safari has no audio/webm support

function pickMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined;
  return CANDIDATE_MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type));
}

/** Compact inline voice-note recorder for task assignment — no full-screen takeover (there's no
 * viewfinder to show, unlike SelfieCapture's camera preview), just a record/stop control that
 * sits inside the existing form grid. */
export function AudioRecorder({ blob, onChange }: { blob: Blob | null; onChange: (blob: Blob | null) => void }) {
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [recording, setRecording] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const preview = blob ? URL.createObjectURL(blob) : null;

  function stopStream() {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
  }

  useEffect(() => () => stopStream(), []);

  async function startRecording() {
    setError(null);
    if (!navigator.mediaDevices?.getUserMedia) { setError('This browser cannot access the microphone. Try Chrome or Safari over HTTPS.'); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = pickMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = () => { onChange(new Blob(chunksRef.current, { type: mimeType ?? 'audio/webm' })); stopStream(); setRecording(false); };
      recorderRef.current = recorder;
      const startedAt = Date.now();
      setElapsedMs(0);
      const tick = setInterval(() => setElapsedMs(Date.now() - startedAt), 250);
      timeoutRef.current = setTimeout(() => recorder.stop(), MAX_RECORDING_MS);
      recorder.addEventListener('stop', () => clearInterval(tick), { once: true });
      recorder.start();
      setRecording(true);
    } catch (err) {
      setError(err instanceof Error && err.name === 'NotAllowedError' ? "Microphone access was blocked. Allow it in your browser's site settings and try again." : 'Could not access the microphone on this device.');
    }
  }

  function stopRecording() {
    recorderRef.current?.stop();
  }

  const mmss = (ms: number) => `${Math.floor(ms / 60_000)}:${String(Math.floor((ms % 60_000) / 1000)).padStart(2, '0')}`;

  if (blob) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <audio controls src={preview!} className="h-10 max-w-full flex-1" />
        <button type="button" className="btn-secondary !px-3" aria-label="Re-record voice note" onClick={() => { onChange(null); void startRecording(); }}><RotateCcw size={16} /></button>
        <button type="button" className="btn-secondary !px-3" aria-label="Remove voice note" onClick={() => onChange(null)}><Trash2 size={16} /></button>
      </div>
    );
  }

  if (recording) {
    return (
      <div className="flex items-center gap-3">
        <span className="flex items-center gap-2 text-sm font-semibold text-red-700"><span className="size-2 animate-pulse rounded-full bg-red-600" /> {mmss(elapsedMs)}</span>
        <button type="button" className="btn-secondary" onClick={stopRecording}><Square size={16} /> Stop</button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-start gap-2">
      <button type="button" className="btn-secondary" onClick={() => void startRecording()}><Mic size={18} /> Record voice note (optional)</button>
      {error && <p className="text-sm text-red-700">{error}</p>}
    </div>
  );
}
